import { TabCountBadge } from '@/components/common/TabCountBadge'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { isWithinTimeRange } from '@/config/utils'
import { cn } from '@/lib/utils'
import {
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import {
	SortableContext,
	arrayMove,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ChevronRight, GripVertical, Trash2 } from 'lucide-react'
import React, { useState, useRef } from 'react'
import type { FilterOption } from '../common-table/FilterSearchBar'
import type { CustomView } from './useCustomViews'
interface CustomViewsDropdownProps {
	views: CustomView[]
	activeViewId: string | null
	onSelectView: (viewId: string) => void
	onDeleteView: (viewId: string) => void
	onClearView: () => void
	data: Record<string, unknown>[]
	onReorderViews?: (views: CustomView[]) => void
	dropdownLabel?: string
	dropdownCount?: number | null
	dropdownRef?: React.Ref<HTMLSpanElement>
	totalTabsCount?: number
	overflowActionTabs?: Array<{
		name: string
		count: number
		key: string
		showCount?: boolean
		onClick?: () => void
		isActive: boolean
	}>
	onOverflowTabClick?: (tab: {
		name: string
		count: number
		key: string
		showCount?: boolean
		onClick?: () => void
		isActive: boolean
	}) => void
	onReorderOverflowTabs?: (
		tabs: Array<{
			name: string
			count: number
			key: string
			showCount?: boolean
			onClick?: () => void
			isActive: boolean
		}>,
	) => void
	contentClassName?: string
}
interface OverflowTab {
	name: string
	count: number
	key: string
	showCount?: boolean
	onClick?: () => void
	isActive: boolean
}

type DropdownItem = { type: 'tab'; data: OverflowTab } | { type: 'view'; data: CustomView }

function SortableItem({
	item,
	activeViewId,
	onSelectView,
	onDeleteView,
	getFilteredLengthForView,
	onTabClick,
	closeDropdown,
}: {
	item: DropdownItem
	activeViewId: string | null
	onSelectView: (viewId: string) => void
	onDeleteView: (viewId: string) => void
	getFilteredLengthForView: (filters: FilterOption[]) => number
	onTabClick?: (tab: OverflowTab) => void
	closeDropdown?: () => void
}) {
	const id = item.type === 'tab' ? item.data.key : item.data.id
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

	const isTab = item.type === 'tab'
	const isActive = isTab ? item.data.isActive : activeViewId === item.data.id
	const name = isTab ? item.data.name : item.data.name
	const count = isTab ? item.data.count : getFilteredLengthForView(item.data.filters)
	const showCountBadge = isTab ? item.data.showCount !== false : true

	const handleClick = () => {
		if (isTab) {
			onTabClick?.(item.data)
			closeDropdown?.()
		} else {
			onSelectView(item.data.id)
			closeDropdown?.()
		}
	}

	return (
		<div
			ref={setNodeRef}
			style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
			className={cn(
				'group flex items-center justify-between px-2 h-(--height-control) rounded-[6px] cursor-pointer text-body!',
				isActive
					? 'bg-header-tab-active-bg text-header-tab-active-text'
					: 'bg-header-tab-inactive-bg text-header-tab-inactive-text group-hover:text-header-tab-active-text',
			)}>
			<div
				{...attributes}
				{...listeners}
				className='opacity-30 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing p-0.5 rounded touch-none'>
				<GripVertical className='size-(--icon-standalone) mr-2' />
			</div>

			<div
				className='flex items-center gap-1.5 flex-1 min-w-0 ml-0.5'
				onClick={handleClick}
				onKeyDown={e => {
					if (e.key === 'Enter' || e.key === ' ') {
						handleClick()
					}
				}}>
				<span className='text-body truncate'>{name}</span>
				<TabCountBadge count={count} active={isActive} showCount={showCountBadge} tooltipSide='right' />
			</div>

			{!isTab && (
				<Button
					variant='ghost'
					size='icon'
					className='size-(--height-control) ml-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity rounded-[6px]'
					onClick={e => {
						e.stopPropagation()
						onDeleteView(item.data.id)
					}}>
					<Trash2 className='size-(--icon-standalone) text-destructive' />
				</Button>
			)}
		</div>
	)
}
// Helper function to calculate filtered data length for a specific view
export const getFilteredLengthForView = (
	data: Record<string, unknown>[],
	viewFilters: FilterOption[],
	orGroups: string[][] = [],
) => {
	if (viewFilters.length === 0) {
		return data.length
	}

	// Group filters by column
	const groupedFilters = viewFilters.reduce<Record<string, FilterOption[]>>((acc, f) => {
		if (!acc[f.column]) {
			acc[f.column] = []
		}
		acc[f.column].push(f)
		return acc
	}, {})

	// Main filtering logic
	const filtered = data.filter(item => {
		const columnIds = Object.keys(groupedFilters)
		const processedColumns = new Set<string>()

		return columnIds.every(columnId => {
			if (processedColumns.has(columnId)) {
				return true
			}

			// Handle OR groups
			const orGroup = orGroups.find(group => group.includes(columnId))
			if (orGroup) {
				for (const orCol of orGroup) {
					processedColumns.add(orCol)
				}

				const orResult = orGroup.some(orCol => {
					if (groupedFilters[orCol]) {
						return matchColumn(item, orCol, groupedFilters[orCol])
					}
					return false
				})
				return orResult
			}

			// Default AND logic
			processedColumns.add(columnId)
			return matchColumn(item, columnId, groupedFilters[columnId])
		})
	})

	return filtered.length
}

// Helper function for column matching
function matchColumn(item: Record<string, unknown>, column: string, colFilters: FilterOption[]) {
	let itemValue: unknown = item[column]

	// Support nested fields like "state.field"
	if (column.includes('.')) {
		const parts = column.split('.')
		itemValue = parts.reduce(
			(obj: unknown, key) =>
				typeof obj === 'object' && obj !== null ? (obj as Record<string, unknown>)[key] : undefined,
			item,
		)
	}

	if (Array.isArray(itemValue)) {
		const values: unknown[] = itemValue
		return colFilters.some(filter => {
			const filterValue = String(filter.value).toLowerCase().trim()

			switch (filter.operator) {
				case 'equals':
					return values.some(val => String(val).toLowerCase().trim() === filterValue)
				case 'notEquals':
					return !values.some(val => String(val).toLowerCase().trim() === filterValue)
				case 'contains':
					return values.some(val => String(val).toLowerCase().trim().includes(filterValue))
				case 'is_null':
					return values.length === 0
				case 'is_not_null':
					return values.length > 0
				default:
					return values.some(val => String(val).toLowerCase().trim() === filterValue)
			}
		})
	}

	const itemValueStr =
		itemValue !== null && itemValue !== undefined
			? typeof itemValue === 'object'
				? JSON.stringify(itemValue)
				: String(itemValue)
			: ''

	const normalizedValue = String(itemValueStr).toLowerCase().trim()

	return colFilters.some(filter => {
		const filterValue = String(filter.value).toLowerCase().trim()

		switch (filter.operator) {
			case 'equals':
				return normalizedValue === filterValue
			case 'notEquals':
				return normalizedValue !== filterValue
			case 'contains':
				return normalizedValue.includes(filterValue)
			case 'is_null':
				return itemValue === null || itemValue === undefined || itemValue === ''
			case 'is_not_null':
				return itemValue !== null && itemValue !== undefined && itemValue !== ''
			case '<':
				return Number.parseFloat(normalizedValue) < Number.parseFloat(filterValue)
			case '<=':
				return Number.parseFloat(normalizedValue) <= Number.parseFloat(filterValue)
			case '>':
				return Number.parseFloat(normalizedValue) > Number.parseFloat(filterValue)
			case '>=':
				return Number.parseFloat(normalizedValue) >= Number.parseFloat(filterValue)
			case 'timeRange':
				return isWithinTimeRange(itemValue as string | number | Date, filter.value)
			default:
				return true
		}
	})
}

export function CustomViewsDropdown({
	views,
	activeViewId,
	onSelectView,
	onDeleteView,
	onClearView,
	data,
	onReorderViews,
	dropdownLabel = `${views.length} more`,
	dropdownCount = null,
	dropdownRef,
	totalTabsCount,
	overflowActionTabs,
	onOverflowTabClick,
	onReorderOverflowTabs,
	contentClassName,
}: CustomViewsDropdownProps) {
	const [localViews, setLocalViews] = useState(views)
	const [isOpen, setIsOpen] = useState(false)

	// Track if we just reordered to prevent useEffect from resetting
	const justReorderedRef = useRef(false)

	React.useEffect(() => {
		// Skip reset if we just performed a reorder operation
		if (justReorderedRef.current) {
			return
		}
		setLocalViews(views)
	}, [views])

	//  Drag and drop sensors
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 5, // Require 5px movement before activating drag
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	)
	// Inside CustomViewsDropdown component:

	// Combine tabs and views into unified list
	const combinedItems: DropdownItem[] = [
		...(overflowActionTabs?.map(tab => ({ type: 'tab' as const, data: tab })) || []),
		...localViews.map(view => ({ type: 'view' as const, data: view })),
	]

	const [localItems, setLocalItems] = useState(combinedItems)

	React.useEffect(() => {
		// Skip reset if we just performed a reorder operation
		if (justReorderedRef.current) {
			justReorderedRef.current = false
			return
		}

		const combined = [
			...(overflowActionTabs?.map(tab => ({ type: 'tab' as const, data: tab })) || []),
			...views.map(view => ({ type: 'view' as const, data: view })),
		]
		setLocalItems(combined)
	}, [views, overflowActionTabs])

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event

		if (over && active.id !== over.id) {
			// Mark that we just reordered to prevent useEffect from resetting
			justReorderedRef.current = true

			// Calculate new order from current localItems
			const oldIndex = localItems.findIndex(item =>
				item.type === 'tab' ? item.data.key === active.id : item.data.id === active.id,
			)
			const newIndex = localItems.findIndex(item =>
				item.type === 'tab' ? item.data.key === over.id : item.data.id === over.id,
			)

			if (oldIndex === -1 || newIndex === -1) {
				return
			}

			const newOrder = arrayMove(localItems, oldIndex, newIndex)

			// Update local state
			setLocalItems(newOrder)

			// Call parent handlers AFTER state update (not inside setState)
			const reorderedTabs = newOrder
				.filter((item): item is { type: 'tab'; data: OverflowTab } => item.type === 'tab')
				.map(item => item.data)

			const reorderedViews = newOrder
				.filter((item): item is { type: 'view'; data: CustomView } => item.type === 'view')
				.map(item => item.data)

			if (onReorderOverflowTabs && reorderedTabs.length > 0) {
				onReorderOverflowTabs(reorderedTabs)
			}

			if (onReorderViews && reorderedViews.length > 0) {
				onReorderViews(reorderedViews)
			}
		}
	}

	if (totalTabsCount === undefined || totalTabsCount <= 3) {
		return null
	}

	return (
		<DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
			<DropdownMenuTrigger asChild>
				{totalTabsCount !== undefined && totalTabsCount > 3 && (
					<span
						ref={dropdownRef}
						className={cn(
							'h-control border-dashed flex gap-1.5 items-center px-3 rounded-[6px] text-body cursor-pointer select-none hover:text-foreground transition-colors',
							dropdownCount !== null
								? 'bg-header-tab-active-bg border-none text-header-tab-active-text'
								: 'bg-transparent text-header-tab-inactive-text',
						)}
						data-testid='custom-views-dropdown-trigger'>
						<span
							className='truncate max-w-32 sm:max-w-48 md:max-w-64'
							title={dropdownLabel}
							data-testid='custom-views-dropdown-label'>
							{dropdownLabel}
						</span>
						<TabCountBadge
							count={dropdownCount}
							active={true}
							showCount={dropdownCount !== null}
							className='ml-0 h-5 min-w-5 px-1.5 text-body font-bold'
							data-testid='custom-views-dropdown-count'
						/>
						<ChevronRight className='size-(--icon-standalone) ml-1.5' />
					</span>
				)}
			</DropdownMenuTrigger>
			<DropdownMenuContent align='start' className={cn('w-max max-h-[300px] overflow-y-auto', contentClassName)}>
				{localItems.length > 0 && (
					<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
						<SortableContext
							items={localItems.map(item => (item.type === 'tab' ? item.data.key : item.data.id))}
							strategy={verticalListSortingStrategy}>
							{localItems.map(item => (
								<SortableItem
									key={item.type === 'tab' ? item.data.key : item.data.id}
									item={item}
									activeViewId={activeViewId}
									onSelectView={onSelectView}
									onDeleteView={onDeleteView}
									getFilteredLengthForView={filters => getFilteredLengthForView(data, filters)}
									onTabClick={onOverflowTabClick}
									closeDropdown={() => setIsOpen(false)}
								/>
							))}
						</SortableContext>
					</DndContext>
				)}

				{activeViewId && (
					<>
						<DropdownMenuSeparator />
						<DropdownMenuItem className='text-muted-foreground cursor-pointer' onSelect={onClearView}>
							Clear active view
						</DropdownMenuItem>
					</>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
